import ApiService from './apiService';
import { API_BASE_URL } from '../config/runtimeConfig';

class UserService {
  // Register a new user
  static async register(userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          password: userData.password,
          dateOfBirth: userData.dateOfBirth,
          referralCode: userData.referralCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle structured error responses
        if (data.errors && Array.isArray(data.errors)) {
          const errorMessages = data.errors.map(err => err.message).join(', ');
          throw new Error(errorMessages);
        } else if (data.message) {
          throw new Error(data.message);
        } else {
          throw new Error('Registration failed');
        }
      }

      // Store token in localStorage
      localStorage.setItem('token', data.token);
      
      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Login user
  static async login(credentials) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.errors?.[0]?.msg || 'Login failed');
      }

      // Store token in localStorage
      localStorage.setItem('token', data.token);
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Get current user profile
  static async getCurrentUser() {
    try {
      const response = await ApiService.profileRequest('/profile', {
        method: 'GET',
      });

      return await ApiService.handleResponse(response);
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  // Get user profile by ID
  static async getUserById(userId) {
    try {
      const response = await ApiService.profileRequest(`/user/${userId}`, {
        method: 'GET',
      });

      return await ApiService.handleResponse(response);
    } catch (error) {
      console.error('Get user by ID error:', error);
      throw error;
    }
  }

  // Update user profile
  static async updateProfile(profileData) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${API_BASE_URL}/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update profile');
      }

      return data;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  // Complete onboarding - specialized method for onboarding flow
  static async completeOnboarding(onboardingData) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      // Separate user-level fields (like name) from profile fields
      const userFields = {};
      if (onboardingData.name) {
        userFields.name = onboardingData.name;
      }

      // Transform onboarding data to match backend schema
      const profileData = {
        publicProfile: onboardingData.publicProfile || {},
        generalDatingPreferences: onboardingData.generalDatingPreferences || {},
        partnerIdeals: onboardingData.partnerIdeals || {},
        basicInfo: onboardingData.basicInfo || {},
        // Mark onboarding as complete
        privateData: {
          ...onboardingData.privateData,
          onboardingCompleted: true,
          onboardingCompletedAt: new Date().toISOString()
        }
      };

      // Update user-level fields if needed (like name)
      let updatedUserData = null;
      if (Object.keys(userFields).length > 0) {
        const userResponse = await fetch(`${API_BASE_URL}/auth/account`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userFields),
        });

        if (!userResponse.ok) {
          const userData = await userResponse.json();
          console.warn('Failed to update user fields:', userData);
        } else {
          const result = await userResponse.json();
          updatedUserData = result.user;
          console.log('User fields updated successfully:', updatedUserData);
        }
      }

      // Update profile data
      const response = await fetch(`${API_BASE_URL}/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to complete onboarding');
      }

      // Mark onboarding as complete in User model
      await fetch(`${API_BASE_URL}/auth/complete-onboarding`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Fetch minimal account fields (name/username/phone) to ensure those are in sync
      const accountResponse = await fetch(`${API_BASE_URL}/auth/account`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      let finalUserData = updatedUserData;
      if (accountResponse.ok) {
        const accountData = await accountResponse.json();
        finalUserData = accountData.user || accountData;
        console.log('Fetched complete user account:', finalUserData);
      }

      // Merge full updated profile (data) with minimal account fields (finalUserData)
      // and return a single user object so reducers persist all fields (including basicInfo)
      const mergedUser = finalUserData ? { ...data, ...finalUserData } : data;
      return mergedUser;
    } catch (error) {
      console.error('Complete onboarding error:', error);
      throw error;
    }
  }

  // Block user
  static async blockUser(userId, payload = {}) {
    try {
      const response = await ApiService.profileRequest(`/user/${userId}/block`, {
        method: 'POST',
        body: JSON.stringify({ reason: payload.reason || null })
      });

      return await ApiService.handleResponse(response);
    } catch (error) {
      console.error('Block user error:', error);
      throw error;
    }
  }

  // Report user
  static async reportUser(userId, reason, description = '') {
    try {
      const response = await ApiService.profileRequest(`/user/${userId}/report`, {
        method: 'POST',
        body: JSON.stringify({ reason, description })
      });

      return await ApiService.handleResponse(response);
    } catch (error) {
      console.error('Report user error:', error);
      throw error;
    }
  }

  // Logout user
  static logout() {
    console.log("UserService.logout() called");
    
    // Clear all authentication-related data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userInfo');
    
    // Clear Redux Persist state
    localStorage.removeItem('persist:root');
    
    // Clear session storage as well
    sessionStorage.clear();
    
    console.log("UserService.logout() completed");
  }

  // Check if user is authenticated
  static isAuthenticated() {
    return !!localStorage.getItem('token');
  }

  // Get stored token
  static getToken() {
    return localStorage.getItem('token');
  }

  // Set token
  static setToken(token) {
    localStorage.setItem('token', token);
  }

  // Update avatar
  static async updateAvatar(avatarUrl) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${API_BASE_URL}/avatar`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ avatar: avatarUrl }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update avatar');
      }

      return data;
    } catch (error) {
      console.error('Update avatar error:', error);
      throw error;
    }
  }

  // Update privacy settings
  static async updatePrivacySettings(privacySettings) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${API_BASE_URL}/privacy`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ privacySettings }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update privacy settings');
      }

      return data;
    } catch (error) {
      console.error('Update privacy settings error:', error);
      throw error;
    }
  }

  // Update security settings
  static async updateSecuritySettings(securitySettings) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${API_BASE_URL}/security`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ securitySettings }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update security settings');
      }

      return data;
    } catch (error) {
      console.error('Update security settings error:', error);
      throw error;
    }
  }

  // Change password
  static async changePassword(currentPassword, newPassword) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${API_BASE_URL}/change-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to change password');
      }

      return data;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  // Upload image (for avatar and profile images)
  static async uploadImage(file) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${API_BASE_URL}/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload image');
      }

      return data;
    } catch (error) {
      console.error('Upload image error:', error);
      throw error;
    }
  }

  // Verify OTP
  static async verifyOTP(otpData) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(otpData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'OTP verification failed');
      }

      // Store token in localStorage
      localStorage.setItem('token', data.token);
      
      return data;
    } catch (error) {
      console.error('OTP verification error:', error);
      throw error;
    }
  }

  // XP Service Methods
  static async awardXP(eventType, metadata = {}) {
    try {
      // Validate token before making request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch(`${API_BASE_URL}/xp/award`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          eventType,
          metadata
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Differentiate between network and server errors
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else {
          throw new Error(errorData.error || 'Failed to award XP');
        }
      }

      return await response.json();
    } catch (error) {
      // Differentiate error types for better user feedback
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error during XP award:', error);
        throw new Error('Network error. Please check your connection and try again.');
      } else {
        console.error('XP award error:', error);
        throw error;
      }
    }
  }

  static async getXPProfile() {
    try {
      // Validate token before making request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch(`${API_BASE_URL}/xp/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Differentiate between network and server errors
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else {
          throw new Error(errorData.error || 'Failed to get XP profile');
        }
      }

      return await response.json();
    } catch (error) {
      // Differentiate error types for better user feedback
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error during XP profile fetch:', error);
        throw new Error('Network error. Please check your connection and try again.');
      } else {
        console.error('Get XP profile error:', error);
        throw error;
      }
    }
  }

  static async getXPHistory(page = 1, limit = 20) {
    try {
      // Validate token before making request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Validate and sanitize pagination parameters
      const validPage = typeof page === 'number' && page > 0 ? page : 1;
      const validLimit = typeof limit === 'number' && limit > 0 && limit <= 100 ? limit : 20;
      
      const response = await fetch(`${API_BASE_URL}/xp/history?page=${validPage}&limit=${validLimit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Differentiate between network and server errors
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else {
          throw new Error(errorData.error || 'Failed to get XP history');
        }
      }

      return await response.json();
    } catch (error) {
      // Differentiate error types for better user feedback
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error during XP history fetch:', error);
        throw new Error('Network error. Please check your connection and try again.');
      } else {
        console.error('Get XP history error:', error);
        throw error;
      }
    }
  }
}

export default UserService;
